/**
 * Table style store: the Query Console's table look, per user and browser.
 *
 * Same Zustand `persist` pattern as uiStore / queryConsoleLayoutStore. The
 * choice drives the console's on-screen tables and its Excel exports.
 * Values read back from storage are normalized, so an old or corrupted
 * entry falls back to Classic grid instead of breaking the console.
 */
import { create } from "zustand"
import { persist } from "zustand/middleware"
import {
  DEFAULT_TABLE_STYLE_CHOICE,
  normalizeTableStyleChoice,
  TableStyleChoice,
} from "types/tableStyle.types"

interface TableStyleStore {
  /** The console's table style. Default: Classic grid. */
  consoleChoice: TableStyleChoice
  /** Sets the console's table style; undefined or invalid resets it to Classic grid. */
  setConsoleChoice: (choice: TableStyleChoice | undefined) => void
}

export const useTableStyleStore = create<TableStyleStore>()(
  persist(
    (set) => ({
      consoleChoice: DEFAULT_TABLE_STYLE_CHOICE,
      setConsoleChoice: (choice) => {
        set({
          consoleChoice:
            normalizeTableStyleChoice(choice) ?? DEFAULT_TABLE_STYLE_CHOICE,
        })
      },
    }),
    {
      name: "dataq-table-style",
      partialize: (state) => ({ consoleChoice: state.consoleChoice }),
      merge: (persisted, current) => ({
        ...current,
        consoleChoice:
          normalizeTableStyleChoice(
            (persisted as { consoleChoice?: unknown } | undefined)?.consoleChoice
          ) ?? DEFAULT_TABLE_STYLE_CHOICE,
      }),
    }
  )
)
